/**
 * @voltxampere/web — DC analysis store (Sprint 14)
 *
 * Holds the latest DCResult-shaped payload from the engine
 * worker plus its pending / error twins. The inspector reads
 * `currentDCResult` (and the spinner / error states) via
 * `SignalController`; the canvas's `probe-binding` effect maps
 * the same signal into `probe-overlay` SceneNode labels.
 *
 * Sprint 14 ships an in-process worker stub: `runDc(circuit)`
 * resolves a promise produced by the registered `dcRunner`
 * function. The shell's `main.ts` registers the real engine
 * worker client at boot. Tests inject a fake runner directly.
 *
 * Sprint 15 will keep this surface but switch the runner to
 * shared transient + AC store entries.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import type { Circuit } from "@voltxampere/schema";

import { t } from "../i18n/index.js";
import { showToast } from "./toasts.js";
import { gateCircuitForEngine } from "./validation-gate.js";

export interface DCResultLike {
  /** True when the solver reached the configured tolerance. */
  readonly converged: boolean;
  /** Total Newton iterations spent. */
  readonly iterations: number;
  /** Net id → DC node voltage [V]. */
  readonly nodeVoltages: Readonly<Record<string, number>>;
  /** Branch id → DC current [A]. */
  readonly branchCurrents: Readonly<Record<string, number>>;
  /** Solver-emitted advisory strings (trivial OP, slow source step, ...). */
  readonly warnings: readonly string[];
}

export const currentDCResult = signal<DCResultLike | null>(null);
export const dcRunning = signal<boolean>(false);
export const dcError = signal<string | null>(null);

export type DcRunner = (circuit: Circuit) => Promise<DCResultLike>;

let runner: DcRunner | null = null;

// Sprint 25 R3 lane 3 — concurrent-run guard. A second `runDc`
// call before the first one settles aborts the first; the first
// one's resolution is dropped on the floor so `currentDCResult`
// always reflects the most recent click. Mirrors the pattern in
// `runTransient` (live mode already used an AbortController) and
// `runAC`.
let activeRunAbort: AbortController | null = null;

/**
 * Inject the runner the shell uses for `runDc()`. `main.ts` wires
 * the real engine worker; tests pass a fake that resolves a known
 * shape. Calling `setDcRunner(null)` resets the surface so a fresh
 * test starts clean.
 */
export function setDcRunner(next: DcRunner | null): void {
  runner = next;
}

/** True when a runner has been registered. */
export function hasDcRunner(): boolean {
  return runner !== null;
}

/**
 * Trigger a DC analysis. Updates `dcRunning`, then resolves the
 * runner Promise into `currentDCResult` or `dcError`. Does NOT
 * throw — the inspector renders the error message from
 * `dcError`.
 */
export async function runDc(circuit: Circuit): Promise<void> {
  if (!runner) {
    dcError.value = "DC runner not registered";
    return;
  }
  // Sprint 25 R2 lane 2 — pre-engine validation gate (TS-008).
  if (!gateCircuitForEngine(circuit, { warnings: true })) {
    return;
  }
  // Sprint 25 R3 lane 3 — abort any in-flight runDc so a fast
  // F5×2 doesn't produce two parallel solves with non-deterministic
  // currentDCResult / dcRunning toggling.
  if (activeRunAbort !== null) {
    activeRunAbort.abort();
  }
  const ctl = new AbortController();
  activeRunAbort = ctl;
  dcRunning.value = true;
  dcError.value = null;
  try {
    const result = await runner(circuit);
    if (ctl.signal.aborted) return;
    currentDCResult.value = result;
    // Sprint 26 Lane 2 (R5-LAT-1) — F5 idempotent re-press
    // feedback. Surface a success toast on every completed DC
    // run so a second F5 press confirms the solver re-ran even
    // when the new operating point matches the previous one
    // bit-for-bit (the inspector tile values would otherwise
    // appear unchanged and the user could not tell whether the
    // keystroke registered).
    showToast({
      kind: "success",
      title: t("toast.dc.refreshed.title"),
      message: t("toast.dc.refreshed.body"),
    });
  } catch (err) {
    if (ctl.signal.aborted) return;
    dcError.value = err instanceof Error ? err.message : "Unknown DC error";
  } finally {
    if (activeRunAbort === ctl) {
      activeRunAbort = null;
      dcRunning.value = false;
    }
  }
}
